import { Link, Outlet } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/hooks/use-auth";
import { LoadingBlock, Container } from "@/components/atoms";

/**
 * noindex for the WHOLE /admin subtree, mounted here rather than in AdminLayout on purpose:
 * AdminLayout only ever renders for an actual admin, whereas the "please sign in" block below
 * renders for everyone else AT THE /admin/* URL — this guard does not redirect (DRB-37, and
 * deliberately so). Those 13 near-identical thin pages all shared the site-wide homepage title
 * and were crawlable, because robots.txt was `Allow: /` with zero Disallow lines.
 */
const AdminNoIndex = () => (
  <Helmet>
    <meta name="robots" content="noindex, nofollow" />
    <title>Admin — DrBurns’ ReLeaf</title>
  </Helmet>
);

// Gated by the Firebase Auth `admin: true` custom claim. No self-promotion (set via Cloud Function).
//
// DRB-37: this used to be `if (!isAdmin) return <Navigate to="/" replace />` — a SILENT bounce to
// the home page with no message at all. That made three completely different situations look
// identical from the user's side: not signed in, signed in without the claim, and signed in
// correctly but sent to /account instead of /admin. Dr. Burns reported this for weeks as
// "I still can not log in as an Admin", and the silence is precisely why it could not be
// diagnosed from her description. Say which of the three it is.
export function AdminGuard() {
  const { loading, isAdmin, user } = useAuth();

  if (loading)
    return (
      <>
        <AdminNoIndex />
        <LoadingBlock label="Verifying admin access…" />
      </>
    );
  if (isAdmin)
    return (
      <>
        <AdminNoIndex />
        <Outlet />
      </>
    );

  const signedOut = !user;
  return (
    <Container className="max-w-md py-16 text-center">
      <AdminNoIndex />
      <h1 className="font-condensed text-2xl font-bold text-night-blue">
        {signedOut
          ? "Please sign in to continue"
          : "This account does not have admin access"}
      </h1>
      <p className="mt-3 text-night-blue/70">
        {signedOut ? (
          "The dashboard is only available once you are signed in."
        ) : (
          <>
            You are signed in as{" "}
            <span className="font-bold text-night-blue">{user?.email}</span>,
            but that account is not marked as an administrator. If this is
            unexpected, please get in touch and we will sort it out.
          </>
        )}
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link
          to={signedOut ? "/account/sign-in" : "/account"}
          className="rounded-soft bg-calm-orange px-6 py-3 text-sm font-bold text-white"
        >
          {signedOut ? "Sign in" : "Go to my account"}
        </Link>
        <a
          href="mailto:orders@drburnsreleaf.com"
          className="rounded-soft border-2 border-night-blue/15 px-6 py-3 text-sm font-bold text-night-blue"
        >
          Contact us
        </a>
      </div>
    </Container>
  );
}
